import { logError } from './error-tracker';

type TelemetryPayload = Record<string, unknown>;

const SESSION_ID_KEY = 'tension_telemetry_session_id';
const SENSITIVE_KEYS = [
  'email',
  'name',
  'phone',
  'address',
  'password',
  'token',
  'secret',
  'key',
  'message',
  'notes',
  'journal',
];

function telemetryEnabled(): boolean {
  return import.meta.env.VITE_TELEMETRY_ENABLED !== 'false';
}

function safeNowIso(): string {
  return new Date().toISOString();
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const next = globalThis.crypto?.randomUUID?.() ?? `telemetry_${Date.now()}`;
    sessionStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch {
    return 'session_unavailable';
  }
}

function getDistinctId(): string {
  try {
    const raw = localStorage.getItem('tension_user');
    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string };
      if (typeof parsed?.id === 'string' && parsed.id.length > 0) return parsed.id;
    }
  } catch {
    // ignore
  }
  return `anon_${getSessionId()}`;
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEYS.some((s) => lower.includes(s));
}

function sanitizeValue(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === 'string') return value.length > 180 ? `${value.slice(0, 180)}...` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map((v) => sanitizeValue(v));
  if (typeof value === 'object') return sanitizePayload(value as TelemetryPayload);
  return String(value);
}

function sanitizePayload(payload: TelemetryPayload): TelemetryPayload {
  const out: TelemetryPayload = {};
  for (const [key, value] of Object.entries(payload)) {
    if (isSensitiveKey(key)) continue;
    out[key] = sanitizeValue(value);
  }
  return out;
}

function parseSentryDsn(dsn: string): { endpoint: string; publicKey: string } | null {
  try {
    const url = new URL(dsn);
    const projectId = url.pathname.split('/').filter(Boolean).pop();
    if (!projectId || !url.username) return null;
    const endpoint = `${url.protocol}//${url.host}/api/${projectId}/store/`;
    return { endpoint, publicKey: url.username };
  } catch {
    return null;
  }
}

async function sendToPosthog(event: string, properties: TelemetryPayload): Promise<void> {
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!key) return;
  const host = (import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://app.posthog.com').replace(/\/+$/, '');
  await fetch(`${host}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: key,
      event,
      distinct_id: getDistinctId(),
      properties,
      timestamp: safeNowIso(),
    }),
    keepalive: true,
  });
}

async function sendToSentry(error: Error, context: TelemetryPayload): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) return;
  const parsed = parseSentryDsn(dsn);
  if (!parsed) return;
  await fetch(`${parsed.endpoint}?sentry_key=${encodeURIComponent(parsed.publicKey)}&sentry_version=7`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: error.message,
      level: 'error',
      platform: 'javascript',
      timestamp: safeNowIso(),
      extra: context,
      tags: {
        app: 'tension-web',
        env: import.meta.env.MODE,
      },
      exception: {
        values: [
          {
            type: error.name || 'Error',
            value: error.message,
            stacktrace: error.stack ? { frames: [] } : undefined,
          },
        ],
      },
    }),
    keepalive: true,
  });
}

export async function trackTelemetryEvent(event: string, payload: TelemetryPayload = {}): Promise<void> {
  if (!telemetryEnabled()) return;
  const normalized = sanitizePayload({
    ...payload,
    app_env: import.meta.env.MODE,
    session_id: getSessionId(),
  });
  try {
    await sendToPosthog(event, normalized);
  } catch (error) {
    if (import.meta.env.DEV) console.warn('Telemetry event sink failed', { event, error });
  }
}

export async function trackTelemetryError(error: unknown, context: TelemetryPayload = {}): Promise<void> {
  const wrapped = error instanceof Error ? error : new Error(String(error));
  const normalized = sanitizePayload({
    ...context,
    app_env: import.meta.env.MODE,
    session_id: getSessionId(),
  });
  try {
    await sendToSentry(wrapped, normalized);
    await sendToPosthog('client_error', {
      ...normalized,
      error_name: wrapped.name,
      error_message: wrapped.message,
    });
  } catch (sinkError) {
    if (import.meta.env.DEV) console.warn('Telemetry error sink failed', sinkError);
  }
  try {
    logError(wrapped, { ...normalized, tags: ['telemetry'] });
  } catch {
    // ignore secondary logging issues
  }
}

export function createCorrelationId(prefix = 'corr'): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return `${prefix}_${suffix}`;
}
